import React from 'react';
import classNames from 'classnames';
import styles from './InputText.module.scss';

interface InputTextProps extends React.InputHTMLAttributes<HTMLInputElement> {
	inputClassName?: string;
	label: string | React.ReactNode;
	prefix?: string;
	suffix?: string;
	hideLabel?: boolean;
	isSmall?: boolean;
	variant?: 'default' | 'inverse';
	errorText?: string;
	helperText?: string;
	isHidden?: boolean;
}

/**
 * Generic InputText component, used for creating input fields of type e.g. text, password, tel, etc.
 */
export const InputText: React.FunctionComponent<InputTextProps> = ({
	className,
	inputClassName,
	id,
	label,
	prefix,
	suffix,
	hideLabel,
	isSmall,
	variant = 'default',
	disabled,
	required,
	errorText,
	helperText,
	isHidden,
	...rest
}) => {
	const renderInput = (hasPrefix: boolean, hasSuffix: boolean): React.ReactNode => {
		return (
			<input
				className={classNames(
					styles.input,
					{ [styles.hasPrefix]: hasPrefix, [styles.hasSuffix]: hasSuffix },
					inputClassName,
				)}
				id={id}
				disabled={disabled}
				required={required}
				{...rest}
			/>
		);
	};

	return (
		<div
			className={classNames(
				styles.wrapper,
				{
					[styles.isDisabled]: disabled,
					[styles.isSmall]: isSmall,
					[styles.isInverse]: variant === 'inverse',
					[styles.hasError]: !!errorText?.length,
					[styles.isHidden]: isHidden,
				},
				className,
			)}
		>
			<label
				className={classNames(styles.label, { [styles.required]: required, ['u-hide-visually']: hideLabel })}
				htmlFor={id}
			>
				{label}
			</label>

			{prefix || suffix ? (
				<div className={styles.inputWrapper}>
					{prefix ? <span className={classNames(styles.affix, styles.isPrefix)}>{prefix}</span> : null}
					{renderInput(Boolean(prefix), Boolean(suffix))}
					{suffix ? <span className={classNames(styles.affix, styles.isSuffix)}>{suffix}</span> : null}
				</div>
			) : (
				renderInput(false, false)
			)}

			{helperText && <p className={styles.helperText}>{helperText}</p>}
			{errorText && <p className={styles.errorText}>{errorText}</p>}
		</div>
	);
};
